import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API_BASE } from "./AuthContext";
import NotificationsBell from "./NotificationsBell";
import TrialBanner from "./TrialBanner";
import FloatingTimerWidget from "./FloatingTimerWidget";
import IdGapHost from "./IdGapHost";
import { LayoutDashboard, Users, Briefcase, Clock, BookOpenText, LogOut, GitBranch, Sparkles, Inbox, FileSignature, Receipt, FileText, Mail, BarChart3, Wand2, MailSearch, ShieldCheck, ListChecks, MailPlus, Calculator, TrendingUp, CreditCard, Building2, UserPlus, CheckSquare, Plug, ChevronRight, Layers, Settings as SettingsIcon, ChevronDown, Share2, Contact2 } from "lucide-react";
import { logError } from "../lib/log";

// Primary navigation — high-frequency, operational items.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: <LayoutDashboard size={16} />, testid: "nav-dashboard" },
  { to: "/clients",   label: "Clients",   icon: <Users size={16} />,           testid: "nav-clients" },
  { to: "/jobs",      label: "Jobs",      icon: <Briefcase size={16} />,       testid: "nav-jobs" },
  { to: "/insights",  label: "Insights",  icon: <Sparkles size={16} />,        testid: "nav-insights", badgeKey: "insights" },
  { to: "/email-triage", label: "Email triage", icon: <MailSearch size={16} />, testid: "nav-email-triage", badgeKey: "triage" },
  { to: "/leads",     label: "Leads",      icon: <ListChecks size={16} />,    testid: "nav-leads" },
  { to: "/referrals", label: "Referrals",  icon: <Share2 size={16} />,        testid: "nav-referrals" },
  { to: "/tasks",     label: "Tasks",      icon: <CheckSquare size={16} />,   testid: "nav-tasks", badgeKey: "tasks" },
  { to: "/reports",   label: "Reports",    icon: <TrendingUp size={16} />,    testid: "nav-reports" },
  { to: "/file-requests", label: "File requests", icon: <Inbox size={16} />,   testid: "nav-file-requests" },
  { to: "/documents", label: "Documents", icon: <FileText size={16} />,        testid: "nav-documents" },
  { to: "/esign",     label: "eSign",     icon: <FileSignature size={16} />,   testid: "nav-esign" },
  { to: "/esign-docuseal", label: "DocuSeal", icon: <FileSignature size={16} />, testid: "nav-esign-docuseal" },
  { to: "/invoices",  label: "Invoices",  icon: <Receipt size={16} />,         testid: "nav-invoices" },
  { to: "/time",      label: "Time",      icon: <Clock size={16} />,           testid: "nav-time" },
  { to: "/team-time", label: "Team time", icon: <BarChart3 size={16} />,       testid: "nav-team-time" },
];

// Configuration / admin section — grouped under a collapsible "Settings"
// header. Each item is a top-level page but visually nested to reduce
// primary-nav noise (v68.23).
const SETTINGS_NAV = [
  { to: "/team",            label: "Team",           icon: <UserPlus size={16} />,   testid: "nav-team" },
  { to: "/fee-schedule",    label: "Fee schedule",   icon: <Calculator size={16} />, testid: "nav-fee-schedule" },
  { to: "/workflows",       label: "Workflows",      icon: <GitBranch size={16} />,  testid: "nav-workflows" },
  { to: "/email-templates", label: "Templates",      icon: <MailPlus size={16} />,   testid: "nav-email-templates" },
  { to: "/leads/intake-forms", label: "Intake forms", icon: <Layers size={16} />,     testid: "nav-intake-forms" },
  { to: "/referral-partners",  label: "Referral partners", icon: <Contact2 size={16} />, testid: "nav-referral-partners" },
  { to: "/document-rules",  label: "Rules",          icon: <Wand2 size={16} />,      testid: "nav-rules" },
  { to: "/email-log",       label: "Email log",      icon: <Mail size={16} />,       testid: "nav-email-log" },
  { to: "/brief",           label: "Brief",          icon: <BookOpenText size={16} />, testid: "nav-brief", requiresBrief: true },
];

const SUPERADMIN_NAV = [
  { to: "/superadmin", label: "Superadmin", icon: <ShieldCheck size={16} />,    testid: "nav-superadmin" },
];

function useInsightsPendingCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API_BASE}/insights/proposals/pending-count`);
        if (!cancel) setCount(r.data?.count || 0);
      } catch (err) {
        // 401/403 expected for anonymous or no-org users — don't spam the console
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          logError("portal-layout:insights-pending", err);
        }
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    const onRefresh = () => fetchCount();
    window.addEventListener("insights:refresh-pending", onRefresh);
    return () => { cancel = true; clearInterval(id); window.removeEventListener("insights:refresh-pending", onRefresh); };
  }, [enabled]);
  return count;
}

function useTriageUntriagedCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API_BASE}/email-triage/status`);
        if (!cancel) setCount(r.data?.untriaged_count || 0);
      } catch (err) {
        // 401/403 expected for anonymous or no-org users — don't spam the console
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          logError("portal-layout:triage-status", err);
        }
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    const onRefresh = () => fetchCount();
    window.addEventListener("triage:refresh-pending", onRefresh);
    return () => { cancel = true; clearInterval(id); window.removeEventListener("triage:refresh-pending", onRefresh); };
  }, [enabled]);
  return count;
}

function useTasksPendingCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API_BASE}/tasks/summary/counts`);
        if (!cancel) setCount((r.data?.overdue || 0) + (r.data?.today || 0));
      } catch (err) {
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          logError("portal-layout:tasks-summary", err);
        }
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 45000);
    const onRefresh = () => fetchCount();
    window.addEventListener("tasks:refresh-pending", onRefresh);
    return () => { cancel = true; clearInterval(id); window.removeEventListener("tasks:refresh-pending", onRefresh); };
  }, [enabled]);
  return count;
}

function NavItemLink({ n, badges }) {
  return (
    <NavLink
      to={n.to}
      data-testid={n.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-[14px] transition ${
          isActive
            ? "bg-[var(--ink)] text-[var(--paper)]"
            : "text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)]"
        }`
      }
    >
      {n.icon}
      <span className="flex-1">{n.label}</span>
      {n.badgeKey === "insights" && badges.insightsPending > 0 && (
        <span data-testid="nav-insights-badge"
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
              style={{ background: "var(--accent)", color: "var(--paper)" }}>
          {badges.insightsPending}
        </span>
      )}
      {n.badgeKey === "triage" && badges.triagePending > 0 && (
        <span data-testid="nav-triage-badge"
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
              style={{ background: "var(--accent)", color: "var(--paper)" }}>
          {badges.triagePending}
        </span>
      )}
      {n.badgeKey === "tasks" && badges.tasksPending > 0 && (
        <span data-testid="nav-tasks-badge"
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
              style={{ background: "var(--accent)", color: "var(--paper)" }}>
          {badges.tasksPending}
        </span>
      )}
    </NavLink>
  );
}

/**
 * v68.23 — Collapsible "Settings" group. Groups lower-frequency config
 * pages (Team, Fee schedule, Workflows, Templates, Intake forms, Rules,
 * Email log, Brief, Superadmin) under a single click-to-toggle header
 * so the primary sidebar stays focused on operational work.
 *
 * Persists open/closed state in localStorage and auto-opens when the
 * user is on any child route so the active item is always visible.
 */
function SettingsGroup({ items, isSuperadmin, superadminItems }) {
  const allChildPaths = [
    ...items.map((i) => i.to),
    ...(isSuperadmin ? superadminItems.map((i) => i.to) : []),
  ];
  const activeChild = allChildPaths.some(
    (p) => typeof window !== "undefined" && window.location.pathname.startsWith(p),
  );
  const [open, setOpen] = useState(() => {
    if (activeChild) return true;
    try { return localStorage.getItem("nav:settings-open") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("nav:settings-open", open ? "1" : "0"); }
    catch { /* no-op */ }
  }, [open]);
  useEffect(() => { if (activeChild) setOpen(true); }, [activeChild]);

  return (
    <div className="pt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-settings-toggle"
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[14px] transition text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)]"
      >
        <SettingsIcon size={16} />
        <span className="flex-1 text-left">Settings</span>
        <ChevronDown size={13}
                     className={`transition-transform ${open ? "rotate-0" : "-rotate-90"} text-[var(--ink-3)]`} />
      </button>
      {open && (
        <div className="pl-4 mt-1 space-y-0.5 border-l ml-4"
             style={{ borderColor: "var(--border)" }}
             data-testid="nav-settings-group">
          {items.map((n) => (
            <NavItemLink key={n.to} n={n} badges={{}} />
          ))}
          {isSuperadmin && superadminItems.map((n) => (
            <NavItemLink key={n.to} n={n} badges={{}} />
          ))}
        </div>
      )}
    </div>
  );
}


function Initials({ name }) {
  const ini = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-medium"
         style={{ background: "var(--ink)", color: "var(--paper)" }}>
      {ini}
    </div>
  );
}

function useOrgBranding(hasOrg) {
  const [org, setOrg] = useState(null);
  useEffect(() => {
    if (!hasOrg) return;
    let cancel = false;
    axios.get(`${API_BASE}/orgs/me`)
      .then((r) => { if (!cancel) setOrg(r.data); })
      .catch((e) => logError("portal-layout:org-fetch", e));
    return () => { cancel = true; };
  }, [hasOrg]);
  return org;
}

export default function PortalLayout({ children }) {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/login"); };
  const hasOrg = !!state?.user?.org_id;
  const insightsPending = useInsightsPendingCount(hasOrg);
  const triagePending = useTriageUntriagedCount(hasOrg);
  const tasksPending = useTasksPendingCount(hasOrg);
  const isSuperadmin = !!state?.user?.is_superadmin;
  // "Brief" is restricted to the super-admin (mannie@ebta.com.au) — hide for everyone else.
  const SUPERADMIN_EMAILS = new Set(["mannie@ebta.com.au"]);
  const canSeeBrief = isSuperadmin || SUPERADMIN_EMAILS.has((state?.user?.email || "").toLowerCase());
  const primaryNav = NAV;
  const settingsNav = SETTINGS_NAV.filter((n) => !n.requiresBrief || canSeeBrief);
  const org = useOrgBranding(hasOrg);

  return (
    <div className="min-h-screen flex">
      <aside className="w-60 border-r border-[var(--border)] bg-[var(--paper)] flex flex-col py-6 px-4 sticky top-0 h-screen"
             data-testid="portal-sidebar">
        <div className="flex items-center gap-2.5 mb-10 px-2" data-testid="sidebar-brand">
          {org?.logo_url ? (
            <img
              src={org.logo_url}
              alt={org.name || "Org logo"}
              className="w-9 h-9 object-contain rounded"
              data-testid="sidebar-org-logo"
            />
          ) : (
            <div className="w-9 h-9 flex items-center justify-center font-heading italic text-lg"
                 style={{ background: "var(--ink)", color: "var(--paper)", borderRadius: "50%" }}>p</div>
          )}
          <div className="leading-tight">
            <div className="font-heading text-[17px] truncate max-w-[150px]" style={{ fontWeight: 500 }}
                 data-testid="sidebar-org-name">
              {org?.name || "Practice OS"}
            </div>
            <div className="overline text-[9px]">{org?.plan ? `${org.plan} plan` : "Solo + AI build"}</div>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto -mr-2 pr-2">
          {primaryNav.map((n) => (
            <NavItemLink key={n.to} n={n}
                          badges={{ insightsPending, triagePending, tasksPending }} />
          ))}

          {/* v68.23 — Collapsible Settings section for lower-frequency config */}
          <SettingsGroup items={settingsNav}
                          isSuperadmin={isSuperadmin}
                          superadminItems={SUPERADMIN_NAV} />
        </nav>

        {state && state !== false && (
          <div className="border-t border-[var(--border)] pt-4 mt-4">
            <AccountMenu state={state} onLogout={onLogout} />
          </div>
        )}
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {hasOrg && <TrialBanner />}
        {children}
      </main>
      {hasOrg && <FloatingTimerWidget />}
      {hasOrg && <IdGapHost />}
    </div>
  );
}

/**
 * v68.19 — Consolidated Account menu.
 *
 * Combines: Notifications bell + Organization + Services + Accounting sync +
 * Workflow settings + My profile + Security + Billing + Log out into a
 * single popover triggered by clicking the user block at the bottom of the
 * sidebar. Frees up ~7 rows of vertical space, groups all secondary/admin
 * actions under one predictable entry point.
 */
function AccountMenu({ state, onLogout }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };
  const items = [
    { to: "/settings/organization", label: "Organization", icon: Building2, testid: "acct-menu-organization" },
    { to: "/settings/services",     label: "Services",     icon: Briefcase, testid: "acct-menu-services" },
    { to: "/settings/accounting",   label: "Accounting sync", icon: Plug,   testid: "acct-menu-accounting" },
    { to: "/settings/workflow",     label: "Workflow settings", icon: GitBranch, testid: "acct-menu-workflow" },
    { to: "/settings/profile",      label: "My profile",   icon: UserPlus,  testid: "acct-menu-profile" },
    { to: "/settings/security",     label: "Security",     icon: ShieldCheck, testid: "acct-menu-security" },
    { to: "/settings/voi-audit",    label: "ID verification log", icon: ShieldCheck, testid: "acct-menu-voi-audit" },
    { to: "/settings/voi-overrides", label: "ID override sign-off", icon: FileSignature, testid: "acct-menu-voi-overrides" },
    { to: "/settings/billing",      label: "Billing",      icon: CreditCard, testid: "acct-menu-billing" },
  ];

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-trigger"
        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md transition ${
          open ? "bg-[var(--subtle)]" : "hover:bg-[var(--subtle)]"
        }`}
        aria-expanded={open}
      >
        <Initials name={state.user.name} />
        <div className="leading-tight overflow-hidden text-left flex-1 min-w-0">
          <div className="text-[13px] font-medium text-[var(--ink)] truncate">{state.user.name}</div>
          <div className="text-[11px] text-[var(--ink-3)] truncate">{state.org?.name}</div>
        </div>
        <ChevronRight size={14} className={`shrink-0 text-[var(--ink-3)] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div
          ref={menuRef}
          data-testid="account-menu-popover"
          className="absolute z-50 left-full ml-2 bottom-0 w-64 rounded-lg shadow-2xl border border-[var(--border)]"
          style={{ background: "var(--paper)" }}
        >
          <div className="px-4 py-3 border-b border-[var(--border)]">
            <div className="overline text-[9px]">Signed in as</div>
            <div className="text-[13px] font-medium text-[var(--ink)] truncate">{state.user.name}</div>
            <div className="text-[11px] text-[var(--ink-3)] truncate">{state.user.email}</div>
          </div>

          {/* Notifications block: keeps the live bell + popover semantics */}
          {state?.user?.org_id && (
            <div className="px-2 py-2 border-b border-[var(--border)]">
              <div className="overline text-[9px] px-2 mb-1">Activity</div>
              <div className="px-2"><NotificationsBell /></div>
            </div>
          )}

          <div className="py-1">
            <div className="overline text-[9px] px-4 py-1.5">Settings</div>
            {items.map(({ to, label, icon: Icon, testid }) => (
              <button
                key={to}
                onClick={() => go(to)}
                data-testid={testid}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] transition text-left"
              >
                <Icon size={13} className="text-[var(--ink-3)]" />
                <span className="flex-1">{label}</span>
              </button>
            ))}
          </div>

          <div className="border-t border-[var(--border)] py-1">
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              data-testid="nav-logout"
              className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-[var(--ink-2)] hover:text-[var(--accent)] hover:bg-[var(--subtle)] transition text-left"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

